import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { Icon } from '@iconify/react'
import { Navigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useRaspadinhaCampaigns } from '@/contexts/RaspadinhaCampaignsContext'
import { useCampaignTotpStepUp } from '@/contexts/CampaignTotpStepUpContext'
import { useCampanhaRaspadinhaLayout } from '@/layouts/CampanhaRaspadinhaLayout/CampanhaRaspadinhaLayoutContext'
import {
  ContainerTabs,
  ContainerTabsList,
  ContainerTab,
  ContainerTabPanel,
} from '@/components/ContainerTabs'
import {
  ensureCampaignParticipants,
  ensureContemplatedPrizeImages,
  ensureCustomDocuments,
  ensureParticipatingCompanies,
  ensureReadingCriteria,
  pruneContemplatedPrizeDrafts,
  pruneCustomDocumentDrafts,
  ensureDateWinningSchedules,
  hasRaspadinhaCampaignChanges,
  serializeRaspadinhaCampaignSnapshot,
  validateRaspadinhaCampaignSave,
} from '@/lib/raspadinha-campaigns'
import type { RaspadinhaCampaign, RaspadinhaCampaignStatus } from '@/types/raspadinha-campaign'
import { canManageInstitutionCampaigns } from '@/lib/campaign-totp-step-up'
import { canViewCampaignDashboard } from '@/lib/collaborator-permissions'
import {
  assignPendingCustomDocumentFile,
  assignPendingPrizeImageFile,
  assignPendingScalarFile,
  getScratchCampaign,
  getScratchCampaignDashboard,
  hasPendingScratchCampaignMedia,
  type ScratchCampaignMediaFiles,
  type ScratchCampaignDashboardMetrics,
} from '@/lib/api/scratch-campaigns.service'
import { RaspadinhaCriteriosLeituraSection } from './RaspadinhaCriteriosLeituraSection'
import { RaspadinhaEmpresasParticipantesSection } from './RaspadinhaEmpresasParticipantesSection'
import { RaspadinhaGanhadoresSection } from './RaspadinhaGanhadoresSection'
import { RaspadinhaParticipantesSection } from './RaspadinhaParticipantesSection'
import { RaspadinhaMidiaSection } from './RaspadinhaMidiaSection'
import { RaspadinhaRegulamentoSection } from './RaspadinhaRegulamentoSection'
import { RaspadinhaValoresSection } from './RaspadinhaValoresSection'
import raspadinhaLayoutStyles from '@/layouts/CampanhaRaspadinhaLayout/CampanhaRaspadinhaLayout.module.css'
import editStyles from '../CampanhasPage/CampanhaEditPage.module.css'
import listStyles from '../CampanhasPage/CampanhasPage.module.css'

const REMOVED_EDIT_TABS = new Set([
  'pixels',
  'roleta',
  'pergunta',
  'widget',
  'numeros-premiados',
  'combos',
])

const STATUS_OPTIONS: { value: RaspadinhaCampaignStatus; label: string }[] = [
  { value: 'draft', label: 'Rascunho' },
  { value: 'active', label: 'Ativa' },
  { value: 'finished', label: 'Finalizada' },
  { value: 'cancelled', label: 'Cancelada' },
]

export function CampanhaRaspadinhaEditPage() {
  const { guard, isCollaborator, hasPermission } = useAuth()
  const { runWithStepUp } = useCampaignTotpStepUp()
  const { getById, updateCampaign } = useRaspadinhaCampaigns()
  const {
    setSaveTrigger,
    setIsSubmitting: setLayoutSubmitting,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    reportSaveSuccess,
  } = useCampanhaRaspadinhaLayout()
  const [params] = useSearchParams()

  const campaignIdParam = params.get('id')
  const campaignId =
    campaignIdParam !== null && Number.isFinite(Number(campaignIdParam))
      ? Number(campaignIdParam)
      : null

  const getInitialTab = useCallback(() => {
    const normalizeTab = (tab: string) => (tab === 'periodo-valores' ? 'contemplacoes' : tab)

    const hashTab = normalizeTab(window.location.hash.replace('#', ''))
    if (hashTab && !REMOVED_EDIT_TABS.has(hashTab)) return hashTab
    const tabParam = params.get('tab')
    if (tabParam) {
      const normalizedTab = normalizeTab(tabParam)
      if (normalizedTab && !REMOVED_EDIT_TABS.has(normalizedTab)) return normalizedTab
    }
    return 'dashboard'
  }, [params])

  const [activeTab, setActiveTab] = useState<string>(getInitialTab)
  const [form, setForm] = useState<RaspadinhaCampaign | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null)
  const [savedSnapshot, setSavedSnapshot] = useState<string | null>(null)
  const [hasPendingGeneratedSchedules, setHasPendingGeneratedSchedules] = useState(false)
  const [hasPendingPeriodChange, setHasPendingPeriodChange] = useState(false)
  const [pendingMedia, setPendingMedia] = useState<ScratchCampaignMediaFiles>({})
  const [dashboardMetrics, setDashboardMetrics] = useState<ScratchCampaignDashboardMetrics | null>(
    null
  )
  const [dashboardLoading, setDashboardLoading] = useState(false)
  const [dashboardError, setDashboardError] = useState<string | null>(null)
  const hasPendingSchedules = hasPendingGeneratedSchedules || hasPendingPeriodChange
  const isReadOnlyExample = form?.is_global_example === true

  const handleWelcomeImageChange = useCallback((url: string, file?: File | null) => {
    setForm((prev) => (prev ? { ...prev, welcome_image_url: url } : prev))
    setPendingMedia((prev) => assignPendingScalarFile(prev, 'welcome_image', file))
  }, [])

  const handleBackgroundImageChange = useCallback((url: string, file?: File | null) => {
    setForm((prev) => (prev ? { ...prev, background_image_url: url } : prev))
    setPendingMedia((prev) => assignPendingScalarFile(prev, 'background_image', file))
  }, [])

  const handleContemplatedImageChange = useCallback((url: string, file?: File | null) => {
    setForm((prev) => (prev ? { ...prev, contemplated_image_url: url } : prev))
    setPendingMedia((prev) => assignPendingScalarFile(prev, 'contemplated_image', file))
  }, [])

  const handleNotContemplatedImageChange = useCallback((url: string, file?: File | null) => {
    setForm((prev) => (prev ? { ...prev, not_contemplated_image_url: url } : prev))
    setPendingMedia((prev) => assignPendingScalarFile(prev, 'not_contemplated_image', file))
  }, [])

  const handlePrizeImageChange = useCallback((index: number, url: string, file?: File | null) => {
    setForm((prev) => {
      if (!prev) return prev
      const contemplated_prize_images = [...prev.contemplated_prize_images]
      if (contemplated_prize_images[index]) {
        contemplated_prize_images[index] = {
          ...contemplated_prize_images[index],
          image_url: url,
        }
      }
      return { ...prev, contemplated_prize_images }
    })
    setPendingMedia((prev) => assignPendingPrizeImageFile(prev, index, file))
  }, [])

  const handleRegulationPdfChange = useCallback((url: string, name: string, file?: File | null) => {
    setForm((prev) =>
      prev
        ? {
            ...prev,
            regulation_content_type: 'document',
            regulation_pdf_url: url,
            regulation_pdf_name: name,
          }
        : prev
    )
    setPendingMedia((prev) => assignPendingScalarFile(prev, 'regulation_pdf', file))
  }, [])

  const handleCookiePolicyPdfChange = useCallback((url: string, name: string, file?: File | null) => {
    setForm((prev) =>
      prev
        ? {
            ...prev,
            regulation_content_type: 'document',
            cookie_policy_pdf_url: url,
            cookie_policy_pdf_name: name,
          }
        : prev
    )
    setPendingMedia((prev) => assignPendingScalarFile(prev, 'cookie_policy_pdf', file))
  }, [])

  const handleCustomDocumentPdfChange = useCallback(
    (index: number, url: string, name: string, file?: File | null) => {
      setForm((prev) => {
        if (!prev) return prev
        const custom_documents = [...(prev.custom_documents ?? [])]
        if (custom_documents[index]) {
          custom_documents[index] = {
            ...custom_documents[index],
            pdf_url: url,
            pdf_name: name,
          }
        }
        return { ...prev, custom_documents }
      })
      setPendingMedia((prev) => assignPendingCustomDocumentFile(prev, index, file))
    },
    []
  )

  const canManage = canManageInstitutionCampaigns(guard, Boolean(isCollaborator))
  const canViewDashboard = canViewCampaignDashboard(Boolean(isCollaborator), hasPermission)

  useEffect(() => {
    if (campaignId === null || !canManage) {
      setIsLoading(false)
      setSavedSnapshot(null)
      setHasUnsavedChanges(false)
      return
    }

    let cancelled = false

    const loadCampaign = async () => {
      setIsLoading(true)
      try {
        let campaign = getById(campaignId)
        if (!campaign) {
          campaign = await getScratchCampaign(campaignId)
        }

        if (cancelled || !campaign) {
          return
        }

        const loadedForm = {
          ...campaign,
          winning_date_schedules: ensureDateWinningSchedules(
            campaign.winning_date_schedules,
            campaign.starts_at,
            campaign.ends_at
          ),
          participants: ensureCampaignParticipants(campaign.participants ?? []),
          participating_companies: ensureParticipatingCompanies(
            campaign.participating_companies ?? []
          ),
          reading_criteria: ensureReadingCriteria(campaign.reading_criteria ?? []),
          contemplated_prize_images: ensureContemplatedPrizeImages(
            campaign.contemplated_prize_images ?? []
          ),
          custom_documents: ensureCustomDocuments(campaign.custom_documents),
        }

        setForm(loadedForm)
        setSavedSnapshot(serializeRaspadinhaCampaignSnapshot(loadedForm))
        setPendingMedia({})
        setHasPendingGeneratedSchedules(false)
        setHasPendingPeriodChange(false)
        setHasUnsavedChanges(false)
      } finally {
        if (!cancelled) {
          setIsLoading(false)
        }
      }
    }

    void loadCampaign()

    return () => {
      cancelled = true
    }
  }, [campaignId, canManage, getById, setHasUnsavedChanges])

  useEffect(() => {
    if (campaignId === null || !canManage || !canViewDashboard) {
      setDashboardMetrics(null)
      setDashboardError(null)
      setDashboardLoading(false)
      return
    }

    let cancelled = false

    const loadDashboard = async () => {
      setDashboardLoading(true)
      setDashboardError(null)
      try {
        const metrics = await getScratchCampaignDashboard(campaignId)
        if (!cancelled) {
          setDashboardMetrics(metrics)
        }
      } catch {
        if (!cancelled) {
          setDashboardError('Não foi possível carregar as métricas do dashboard.')
          setDashboardMetrics(null)
        }
      } finally {
        if (!cancelled) {
          setDashboardLoading(false)
        }
      }
    }

    void loadDashboard()

    return () => {
      cancelled = true
    }
  }, [campaignId, canManage, canViewDashboard])

  useEffect(() => {
    if (!form || !savedSnapshot) {
      setHasUnsavedChanges(false)
      return
    }

    const formDirty = hasRaspadinhaCampaignChanges(form, savedSnapshot)
    setHasUnsavedChanges(formDirty || hasPendingSchedules || hasPendingScratchCampaignMedia(pendingMedia))
  }, [form, hasPendingSchedules, pendingMedia, savedSnapshot, setHasUnsavedChanges])

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!hasUnsavedChanges) {
        return
      }
      event.preventDefault()
      event.returnValue = ''
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [hasUnsavedChanges])

  useEffect(() => {
    return () => {
      setHasUnsavedChanges(false)
    }
  }, [setHasUnsavedChanges])

  useEffect(() => {
    if (activeTab) {
      const newHash = `#${activeTab}`
      if (window.location.hash !== newHash) {
        window.history.replaceState(null, '', newHash)
      }
    }
  }, [activeTab])

  const doSave = useCallback(async () => {
    if (!form || campaignId === null || isReadOnlyExample) return

    setIsSubmitting(true)
    setSubmitError(null)
    setSubmitSuccess(null)

    try {
      const formToSave = {
        ...form,
        contemplated_prize_images: pruneContemplatedPrizeDrafts(form.contemplated_prize_images),
        custom_documents: pruneCustomDocumentDrafts(form.custom_documents ?? []),
      }

      const validationError = validateRaspadinhaCampaignSave(formToSave, {
        hasPendingGeneratedSchedules,
        hasPendingPeriodChange,
      })
      if (validationError) {
        setSubmitError(validationError)
        return
      }

      const updated = await runWithStepUp(() =>
        updateCampaign(
          campaignId,
          {
            ...formToSave,
            starts_at: formToSave.starts_at,
            ends_at: formToSave.ends_at,
          },
          pendingMedia
        )
      )
      const nextForm = {
        ...updated,
        winning_date_schedules: ensureDateWinningSchedules(
          updated.winning_date_schedules,
          updated.starts_at,
          updated.ends_at
        ),
        reading_criteria: ensureReadingCriteria(updated.reading_criteria ?? []),
      }
      setForm(nextForm)
      setSavedSnapshot(serializeRaspadinhaCampaignSnapshot(nextForm))
      setPendingMedia({})
      setHasUnsavedChanges(false)
      reportSaveSuccess()
      setSubmitSuccess('Campanha raspadinha salva com sucesso.')
    } catch (error) {
      setSubmitError(
        error instanceof Error
          ? error.message
          : 'Não foi possível salvar a campanha raspadinha.'
      )
    } finally {
      setIsSubmitting(false)
    }
  }, [
    campaignId,
    form,
    hasPendingGeneratedSchedules,
    hasPendingPeriodChange,
    isReadOnlyExample,
    pendingMedia,
    reportSaveSuccess,
    runWithStepUp,
    setHasUnsavedChanges,
    updateCampaign,
  ])

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      void doSave()
    },
    [doSave]
  )

  useEffect(() => {
    if (!canManage || campaignId === null) return
    setSaveTrigger(() => {
      void doSave()
    })
    return () => setSaveTrigger(null)
  }, [canManage, campaignId, doSave, setSaveTrigger])

  useEffect(() => {
    setLayoutSubmitting(isSubmitting)
  }, [isSubmitting, setLayoutSubmitting])

  if (!canManage) {
    return (
      <div className={editStyles.editPage}>
        <div className={editStyles.editForbidden}>
          <div className={editStyles.editForbiddenMessage} role="alert">
            Apenas instituições podem editar campanhas raspadinha.
          </div>
        </div>
      </div>
    )
  }

  if (campaignId === null) {
    return <Navigate to="/campanha-raspadinha" replace />
  }

  if (!isLoading && !form) {
    return (
      <div className={listStyles.emptyState}>
        <p>Campanha raspadinha não encontrada.</p>
      </div>
    )
  }

  return (
    <div className={`${editStyles.editPage} ${raspadinhaLayoutStyles.editPageWithFixedSave}`}>
      {isReadOnlyExample ? (
        <div className={editStyles.editForbiddenMessage} role="status">
          Campanha de exemplo — somente leitura.
        </div>
      ) : null}

      {isLoading || !form ? (
        <div className={editStyles.editLoadingState}>Carregando campanha raspadinha...</div>
      ) : (
        <form
          id="raspadinha-edit-form"
          className={editStyles.editForm}
          onSubmit={(event) => {
            void handleSubmit(event)
          }}
        >
          {(isSubmitting || submitError || submitSuccess) && (
            <div className={editStyles.editAlerts}>
              {isSubmitting ? (
                <div className={editStyles.editInfoBanner} role="status" aria-live="polite">
                  Salvando campanha raspadinha...
                </div>
              ) : null}
              {submitError ? (
                <div className={editStyles.editErrorBanner} role="alert">
                  {submitError}
                </div>
              ) : null}
              {submitSuccess ? (
                <div className={editStyles.editSuccessBanner} role="status">
                  {submitSuccess}
                </div>
              ) : null}
            </div>
          )}

          <ContainerTabs
            value={activeTab}
            onChange={setActiveTab}
            className={editStyles.containerTabs}
          >
            <ContainerTabsList className={editStyles.containerTabsList}>
              <ContainerTab value="dashboard" icon="mdi:chart-donut">
                Dashboard
              </ContainerTab>
              <ContainerTab value="identidade" icon="mdi:tag-outline">
                Identidade
              </ContainerTab>
              <ContainerTab value="pagina-publica" icon="mdi:web">
                Página Pública
              </ContainerTab>
              <ContainerTab value="comunicacao-midia" icon="mdi:palette-outline">
                Mídia e prêmios
              </ContainerTab>
              <ContainerTab value="contemplacoes" icon="mdi:calendar-clock-outline">
                Contemplações
              </ContainerTab>
              <ContainerTab value="ganhadores" icon="mdi:account-group-outline">
                Ganhadores
              </ContainerTab>
              <ContainerTab value="participantes" icon="mdi:account-multiple-outline">
                Participantes
              </ContainerTab>
              <ContainerTab value="empresas-participantes" icon="mdi:domain">
                Empresas Participantes
              </ContainerTab>
              <ContainerTab value="criterios-leitura" icon="mdi:file-search-outline">
                Critérios de Leitura
              </ContainerTab>
              <ContainerTab value="certificados-regulamento" icon="mdi:file-document-outline">
                Regulamento
              </ContainerTab>
              <ContainerTab value="email" icon="mdi:email-outline">
                E-mail
              </ContainerTab>
            </ContainerTabsList>

            <fieldset
              disabled={isReadOnlyExample}
              className={raspadinhaLayoutStyles.readOnlyFieldset}
            >
            <ContainerTabPanel value="dashboard">
              <section className={editStyles.editSection} aria-labelledby="raspadinha-dashboard">
                <h2 id="raspadinha-dashboard" className={editStyles.editSectionTitle}>
                  <Icon className={editStyles.editSectionTitleIcon} icon="mdi:chart-donut" />
                  Dashboard
                </h2>
                <div className={editStyles.editSectionBody}>
                  {dashboardLoading ? (
                    <p className={editStyles.editLabelHint} role="status">
                      Carregando métricas...
                    </p>
                  ) : null}
                  {dashboardError ? (
                    <p className={editStyles.editErrorBanner} role="alert">
                      {dashboardError}
                    </p>
                  ) : null}
                  {dashboardMetrics ? (
                    <div className={listStyles.statsRow}>
                      <div className={listStyles.statItem}>
                        <div className={listStyles.statContent}>
                          <span>Cartelas vendidas</span>
                          <strong>{dashboardMetrics.cartelas_vendidas}</strong>
                        </div>
                      </div>
                      <div className={listStyles.statItem}>
                        <div className={listStyles.statContent}>
                          <span>Cartelas raspadas</span>
                          <strong>{dashboardMetrics.cartelas_raspadas}</strong>
                        </div>
                      </div>
                      <div className={listStyles.statItem}>
                        <div className={listStyles.statContent}>
                          <span>Horários semanais</span>
                          <strong>{dashboardMetrics.horarios_semanais}</strong>
                        </div>
                      </div>
                      <div className={listStyles.statItem}>
                        <div className={listStyles.statContent}>
                          <span>Ganhadores cadastrados</span>
                          <strong>{dashboardMetrics.ganhadores_cadastrados}</strong>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              </section>
            </ContainerTabPanel>

            <ContainerTabPanel value="identidade">
              <section className={editStyles.editSection} aria-labelledby="raspadinha-identidade">
                <h2 id="raspadinha-identidade" className={editStyles.editSectionTitle}>
                  <Icon className={editStyles.editSectionTitleIcon} icon="mdi:tag-outline" />
                  Identidade
                </h2>
                <div className={editStyles.editSectionBody}>
                  <div className={editStyles.editGrid2}>
                    <div className={editStyles.editField}>
                      <label className={editStyles.editLabel} htmlFor="raspadinha-name">
                        Nome da campanha raspadinha *
                      </label>
                      <input
                        id="raspadinha-name"
                        type="text"
                        className={editStyles.editInput}
                        value={form.name}
                        onChange={(e) => setForm((prev) => (prev ? { ...prev, name: e.target.value } : prev))}
                        required
                      />
                    </div>
                    <div className={editStyles.editField}>
                      <label className={editStyles.editLabel} htmlFor="raspadinha-status">
                        Status
                      </label>
                      <select
                        id="raspadinha-status"
                        className={editStyles.editSelect}
                        value={form.status}
                        onChange={(e) =>
                          setForm((prev) =>
                            prev
                              ? { ...prev, status: e.target.value as RaspadinhaCampaignStatus }
                              : prev
                          )
                        }
                      >
                        {STATUS_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      {form.status !== 'active' ? (
                        <p className={editStyles.editLabelHint}>
                          A página pública em <strong>/raspadinha/{form.slug || 'slug'}</strong>{' '}
                          só fica disponível com status Ativa.
                        </p>
                      ) : null}
                    </div>
                  </div>
                  <div className={editStyles.editField}>
                    <label className={editStyles.editLabel} htmlFor="raspadinha-prize">
                      Descrição dos prêmios da raspadinha
                    </label>
                    <textarea
                      id="raspadinha-prize"
                      className={editStyles.editTextarea}
                      rows={3}
                      value={form.prize_description}
                      onChange={(e) =>
                        setForm((prev) =>
                          prev ? { ...prev, prize_description: e.target.value } : prev
                        )
                      }
                    />
                  </div>
                </div>
              </section>
            </ContainerTabPanel>

            <ContainerTabPanel value="pagina-publica">
              <section className={editStyles.editSection} aria-labelledby="raspadinha-publica">
                <h2 id="raspadinha-publica" className={editStyles.editSectionTitle}>
                  <Icon className={editStyles.editSectionTitleIcon} icon="mdi:web" />
                  Página Pública
                </h2>
                <div className={editStyles.editSectionBody}>
                  <div className={editStyles.editGrid2}>
                    <div className={editStyles.editField}>
                      <label className={editStyles.editLabel} htmlFor="raspadinha-title">
                        Título público
                      </label>
                      <input
                        id="raspadinha-title"
                        type="text"
                        className={editStyles.editInput}
                        value={form.title}
                        onChange={(e) =>
                          setForm((prev) => (prev ? { ...prev, title: e.target.value } : prev))
                        }
                      />
                    </div>
                    <div className={editStyles.editField}>
                      <label className={editStyles.editLabel} htmlFor="raspadinha-slug">
                        Slug (URL amigável)
                      </label>
                      <input
                        id="raspadinha-slug"
                        type="text"
                        className={editStyles.editInput}
                        value={form.slug}
                        onChange={(e) =>
                          setForm((prev) => (prev ? { ...prev, slug: e.target.value } : prev))
                        }
                      />
                    </div>
                  </div>
                  <div className={editStyles.editField}>
                    <label className={editStyles.editLabel} htmlFor="raspadinha-description">
                      Descrição da campanha
                    </label>
                    <textarea
                      id="raspadinha-description"
                      className={editStyles.editTextarea}
                      rows={4}
                      value={form.description}
                      onChange={(e) =>
                        setForm((prev) =>
                          prev ? { ...prev, description: e.target.value } : prev
                        )
                      }
                    />
                  </div>
                  <div className={editStyles.editField}>
                    <label className={editStyles.editLabel} htmlFor="raspadinha-public-url">
                      Redirecionamento externo (opcional)
                    </label>
                    <input
                      id="raspadinha-public-url"
                      type="url"
                      className={editStyles.editInput}
                      placeholder="https://exemplo.com/raspadinha"
                      value={form.public_page_url}
                      onChange={(e) =>
                        setForm((prev) =>
                          prev ? { ...prev, public_page_url: e.target.value } : prev
                        )
                      }
                    />
                  </div>
                </div>
              </section>
            </ContainerTabPanel>

            <ContainerTabPanel value="comunicacao-midia">
              <RaspadinhaMidiaSection
                form={form}
                disabled={isReadOnlyExample}
                onFormChange={(next) => setForm(next)}
                onWelcomeImageChange={handleWelcomeImageChange}
                onBackgroundImageChange={handleBackgroundImageChange}
                onContemplatedImageChange={handleContemplatedImageChange}
                onNotContemplatedImageChange={handleNotContemplatedImageChange}
                onPrizeImageChange={handlePrizeImageChange}
              />
            </ContainerTabPanel>

            <ContainerTabPanel value="contemplacoes">
              <RaspadinhaValoresSection
                form={form}
                onFormChange={(next) => setForm(next)}
                onPendingSchedulesChange={setHasPendingGeneratedSchedules}
                onPendingPeriodChange={setHasPendingPeriodChange}
              />
            </ContainerTabPanel>

            <ContainerTabPanel value="ganhadores">
              <RaspadinhaGanhadoresSection form={form} />
            </ContainerTabPanel>

            <ContainerTabPanel value="participantes">
              <RaspadinhaParticipantesSection form={form} />
            </ContainerTabPanel>

            <ContainerTabPanel value="empresas-participantes">
              <RaspadinhaEmpresasParticipantesSection
                form={form}
                onFormChange={(next) => setForm(next)}
              />
            </ContainerTabPanel>

            <ContainerTabPanel value="criterios-leitura">
              <RaspadinhaCriteriosLeituraSection
                form={form}
                onFormChange={(next) => setForm(next)}
              />
            </ContainerTabPanel>

            <ContainerTabPanel value="certificados-regulamento">
              <RaspadinhaRegulamentoSection
                form={form}
                disabled={isReadOnlyExample}
                onFormChange={(next) => setForm(next)}
                onRegulationPdfChange={handleRegulationPdfChange}
                onCookiePolicyPdfChange={handleCookiePolicyPdfChange}
                onCustomDocumentPdfChange={handleCustomDocumentPdfChange}
              />
            </ContainerTabPanel>

            <ContainerTabPanel value="email">
              <section
                className={editStyles.editSection}
                aria-labelledby="raspadinha-email"
              >
                <h2 id="raspadinha-email" className={editStyles.editSectionTitle}>
                  <Icon
                    className={editStyles.editSectionTitleIcon}
                    icon="mdi:email-outline"
                  />
                  E-mails da plataforma
                </h2>
                <div className={editStyles.editSectionBody}>
                  <p>
                    As comunicações transacionais usam o padrão da plataforma Sortee.
                  </p>
                </div>
              </section>
            </ContainerTabPanel>
            </fieldset>
          </ContainerTabs>
        </form>
      )}
    </div>
  )
}
